// usePermissions Hook - Cached permission checking

import { useState, useEffect, useCallback } from 'react';
import { userManagementService } from '../api/userManagementService';

// Global cache for permissions
let permissionsCache: string[] | null = null;
let permissionsPromise: Promise<string[]> | null = null;

/**
 * Hook to check user permissions with caching
 */
export function usePermissions() {
  const [permissions, setPermissions] = useState<string[]>(permissionsCache || []);
  const [loading, setLoading] = useState(!permissionsCache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPermissions = async () => {
      // If we already have cached permissions, use them
      if (permissionsCache) {
        setPermissions(permissionsCache);
        setLoading(false);
        return;
      }

      // If a request is already in progress, wait for it
      if (permissionsPromise) {
        try {
          const result = await permissionsPromise;
          setPermissions(result);
        } catch (err) {
          setError('Fehler beim Laden der Berechtigungen');
        } finally {
          setLoading(false);
        }
        return;
      }

      // Start a new request
      setLoading(true);
      permissionsPromise = userManagementService.getMyPermissions();

      try {
        const result = await permissionsPromise;
        permissionsCache = result;
        setPermissions(result);
      } catch (err) {
        console.error('Fehler beim Laden der Berechtigungen:', err);
        setError('Fehler beim Laden der Berechtigungen');
        // Fallback: allow all if error
        permissionsCache = ['*'];
        setPermissions(['*']);
      } finally {
        setLoading(false);
        permissionsPromise = null;
      }
    };

    loadPermissions();
  }, []);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permission: string): boolean => {
    if (!permission) return true; // No permission required
    if (permissions.includes('*')) return true; // Fallback: show all
    if (permissions.length === 0) return false; // No permissions = no access
    return permissions.includes(permission);
  }, [permissions]);

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback((permissionList: string[]): boolean => {
    return permissionList.some(p => hasPermission(p));
  }, [hasPermission]);

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = useCallback((permissionList: string[]): boolean => {
    return permissionList.every(p => hasPermission(p));
  }, [hasPermission]);

  /**
   * Clear the cache (useful after login/logout)
   */
  const clearCache = useCallback(() => {
    permissionsCache = null;
    permissionsPromise = null;
  }, []);

  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    clearCache,
  };
}

/**
 * Clear permissions cache (call on logout)
 */
export function clearPermissionsCache() {
  permissionsCache = null;
  permissionsPromise = null;
}
